import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
  fetchBankAccounts,
  addBankAccount,
  updateBankAccount,
  deleteBankAccount,
  setPrimaryBankAccount,
  BankAccount
} from '../../redux/revenueSlice';
import ToastService from '../../core/notifications/ToastService';

interface BankFormData {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  accountType: 'savings' | 'current';
}

interface FormErrors {
  [key: string]: string;
}

export const BankDetailsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { bankAccounts, isLoading, error } = useSelector(
    (state: RootState) => state.revenue
  );

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<BankFormData>({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    accountType: 'savings'
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchBankAccounts());
  }, [dispatch]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Account Holder Name validation
    if (!formData.accountHolderName.trim()) {
      errors.accountHolderName = 'Account holder name is required';
    } else if (formData.accountHolderName.length < 3 || formData.accountHolderName.length > 60) {
      errors.accountHolderName = 'Name must be between 3 and 60 characters';
    }

    // Account Number validation
    if (!formData.accountNumber.trim()) {
      errors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits';
    }

    // IFSC Code validation
    if (!formData.ifscCode.trim()) {
      errors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC code format (e.g., SBIN0001234)';
    }

    // Bank Name validation
    if (!formData.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    }

    // Account Type validation
    if (!['savings', 'current'].includes(formData.accountType)) {
      errors.accountType = 'Please select a valid account type';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      accountType: 'savings'
    });
    setFormErrors({});
    setShowAddDialog(true);
  };

  const handleEditClick = (account: BankAccount) => {
    setEditingId(account.id);
    setFormData({
      accountHolderName: account.accountHolderName,
      accountNumber: account.accountNumber,
      ifscCode: account.ifscCode,
      bankName: account.bankName,
      accountType: account.accountType
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setActionLoading(true);
    try {
      if (editingId) {
        await dispatch(updateBankAccount({
          id: editingId,
          account: formData
        })).unwrap();
        ToastService.success('Bank account updated successfully');
        setShowEditDialog(false);
      } else {
        await dispatch(addBankAccount(formData)).unwrap();
        ToastService.success('Bank account added successfully');
        setShowAddDialog(false);
      }
      dispatch(fetchBankAccounts());
    } catch (err: any) {
      ToastService.error(err || 'Failed to save bank account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) {
      return;
    }

    setActionLoading(true);
    try {
      await dispatch(deleteBankAccount(id)).unwrap();
      ToastService.success('Bank account deleted successfully');
      dispatch(fetchBankAccounts());
    } catch (err: any) {
      ToastService.error(err || 'Failed to delete bank account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPrimary = async (id: number) => {
    setActionLoading(true);
    try {
      await dispatch(setPrimaryBankAccount(id)).unwrap();
      ToastService.success('Primary bank account updated');
      dispatch(fetchBankAccounts());
    } catch (err: any) {
      ToastService.error(err || 'Failed to set primary account');
    } finally {
      setActionLoading(false);
    }
  };

  const maskAccountNumber = (accountNumber: string): string => {
    if (accountNumber.length <= 4) return accountNumber;
    return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4);
  };

  const getVerificationStatus = (account: BankAccount) => {
    const statusColors: Record<string, string> = {
      verified: '#10B981',
      pending: '#F59E0B',
      failed: '#EF4444'
    };
    const statusIcons: Record<string, React.ReactNode> = {
      verified: <CheckIcon sx={{ fontSize: 16 }} />,
      pending: <CloseIcon sx={{ fontSize: 16 }} />,
      failed: <CloseIcon sx={{ fontSize: 16 }} />
    };

    return {
      color: statusColors[account.verificationStatus] || '#6B7280',
      icon: statusIcons[account.verificationStatus]
    };
  };

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Bank Account Details
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage your bank accounts for payouts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ backgroundColor: '#14B8A6' }}
        >
          Add Bank Account
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        Bank accounts must be verified before they can be used for payouts. Verification is done through micro-deposits.
      </Alert>

      {/* Bank Accounts List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : bankAccounts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              No bank accounts added yet
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{ backgroundColor: '#14B8A6' }}
            >
              Add Your First Bank Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {bankAccounts.map((account: BankAccount) => {
            const verificationStatus = getVerificationStatus(account);
            return (
              <Grid item xs={12} md={6} key={account.id}>
                <Card sx={{ position: 'relative' }}>
                  {account.isPrimary && (
                    <Chip
                      label="Primary Account"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: '#14B8A6',
                        color: 'white'
                      }}
                    />
                  )}

                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {account.bankName}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Account Holder
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {account.accountHolderName}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Account Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'monospace' }}>
                        {maskAccountNumber(account.accountNumber)}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        IFSC Code
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'monospace' }}>
                        {account.ifscCode}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Account Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, textTransform: 'capitalize' }}>
                        {account.accountType}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Verification Status:
                        </Typography>
                        <Chip
                          icon={verificationStatus.icon as any}
                          label={account.verificationStatus}
                          size="small"
                          sx={{
                            backgroundColor: verificationStatus.color + '20',
                            color: verificationStatus.color,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {!account.isPrimary && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSetPrimary(account.id)}
                          disabled={actionLoading}
                        >
                          Set as Primary
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(account)}
                        disabled={actionLoading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(account.id)}
                        disabled={actionLoading}
                        sx={{ color: '#EF4444' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onClose={() => {
        setShowAddDialog(false);
        setShowEditDialog(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Bank Account' : 'Add Bank Account'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Account Holder Name"
              value={formData.accountHolderName}
              onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
              error={!!formErrors.accountHolderName}
              helperText={formErrors.accountHolderName}
            />

            <TextField
              fullWidth
              label="Account Number"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              error={!!formErrors.accountNumber}
              helperText={formErrors.accountNumber}
              placeholder="9-18 digits"
            />

            <TextField
              fullWidth
              label="IFSC Code"
              value={formData.ifscCode}
              onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
              error={!!formErrors.ifscCode}
              helperText={formErrors.ifscCode}
              placeholder="e.g., SBIN0001234"
            />

            <TextField
              fullWidth
              label="Bank Name"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              error={!!formErrors.bankName}
              helperText={formErrors.bankName}
            />

            <TextField
              fullWidth
              select
              label="Account Type"
              value={formData.accountType}
              onChange={(e) => setFormData({ ...formData, accountType: e.target.value as 'savings' | 'current' })}
              SelectProps={{ native: true }}
            >
              <option value="savings">Savings</option>
              <option value="current">Current</option>
            </TextField>

            <Alert severity="warning">
              Ensure all details are correct. You'll need to verify this account before it can be used for payouts.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddDialog(false);
            setShowEditDialog(false);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={actionLoading}
            sx={{ backgroundColor: '#14B8A6' }}
          >
            {actionLoading ? <CircularProgress size={24} /> : (editingId ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BankDetailsPage;
